// Build the hierarchical site navigation menu from a project's flat nav tree,
// for the designer (canvas + inherited-chrome band) so the `nav_menu` block
// previews the real site navigation — pages plus '-' folders as multi-level
// dropdown submenus. Mirrors backend site_from_nav._build_menu and the
// runtime server's _read_site_menu so all surfaces agree.

import { resolveSiteMenus, type SiteMenuItem, type SiteMenuPageRef, type SiteMenus } from '@twasta/page-runtime/renderers'
import type { SiteMenusSpec } from '@twasta/page-schema'

export interface NavItem {
  id?: string
  obj_name?: string
  obj_type?: string
  parent_id?: string
  title?: string
  functional_specs?: string
  order?: number
}

function parseSpec(fs?: string): Record<string, unknown> {
  if (!fs) return {}
  try {
    const o = JSON.parse(fs)
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

function slugify(s: string): string {
  return (s || '').trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

// The External Website (X) node owning this page: nearest X ancestor by
// parent_id, else the primary site, else the first site.
function resolveSiteNode(nav: NavItem[], pageObjName: string): NavItem | null {
  const byId = new Map<string, NavItem>()
  for (const n of nav) if (n.id) byId.set(n.id, n)
  let cur = nav.find((n) => n.obj_name === pageObjName)
  const seen = new Set<string>()
  while (cur && cur.parent_id && !seen.has(cur.parent_id)) {
    seen.add(cur.parent_id)
    const parent = byId.get(cur.parent_id)
    if (!parent) break
    if (parent.obj_type === 'X') return parent
    cur = parent
  }
  const xs = nav.filter((n) => n.obj_type === 'X')
  return xs.find((n) => parseSpec(n.functional_specs).is_primary) || xs[0] || null
}

export function buildSiteMenu(nav: NavItem[] | undefined, pageObjName: string): SiteMenuItem[] {
  if (!Array.isArray(nav) || !nav.length) return []
  const site = resolveSiteNode(nav, pageObjName)
  if (!site) return []
  const spec = parseSpec(site.functional_specs)
  const chrome = new Set(
    [spec.header_page_obj_name, spec.footer_page_obj_name].filter((v): v is string => typeof v === 'string'),
  )
  const byParent = new Map<string, NavItem[]>()
  for (const n of nav) {
    const p = n.parent_id || ''
    if (!byParent.has(p)) byParent.set(p, [])
    byParent.get(p)!.push(n)
  }
  const orderKey = (n: NavItem) => (typeof n.order === 'number' ? n.order : 0)
  const sorted = (pid: string) => (byParent.get(pid) || []).slice().sort((a, b) => orderKey(a) - orderKey(b))

  // Home = explicit home_page_obj_name (if present) else first S descendant.
  const descendants: NavItem[] = []
  const walkAll = (pid: string) => {
    for (const c of sorted(pid)) {
      if (c.obj_type === 'S') descendants.push(c)
      else if (c.obj_type !== 'X') walkAll(c.id || '')
    }
  }
  walkAll(site.id || '')
  const homeObj = (typeof spec.home_page_obj_name === 'string'
    && descendants.some((d) => d.obj_name === spec.home_page_obj_name))
    ? (spec.home_page_obj_name as string)
    : (descendants[0]?.obj_name || '')

  const build = (pid: string): SiteMenuItem[] => {
    const out: SiteMenuItem[] = []
    for (const c of sorted(pid)) {
      if (c.obj_type === 'S') {
        const obj = c.obj_name || ''
        if (!obj || chrome.has(obj)) continue
        out.push({
          type: 'page',
          page_obj_name: obj,
          slug: obj === homeObj ? '' : slugify(obj),
          label: c.title || obj,
        })
      } else if (c.obj_type !== 'X') {
        const sub = build(c.id || '')
        // obj_name lets a nav_menu `root` pin to this folder (Default
        // Header / Default Footer convention).
        if (sub.length) out.push({ type: 'group', obj_name: c.obj_name || '', label: c.title || 'Menu', children: sub })
      }
    }
    return out
  }
  return build(site.id || '')
}

// ── Named site menus (functional_specs.menus) ────────────────────────
// The designer previews a `nav_menu` with `menu: <name>` exactly as the live
// site does: the raw menu spec resolved by page-runtime's resolveSiteMenus
// against this site's page list.

/** The site's routable pages (chrome excluded) with the slug/label/home the
 *  site's `page_overrides` give them. */
export function buildSitePages(nav: NavItem[] | undefined, pageObjName: string): SiteMenuPageRef[] {
  if (!Array.isArray(nav) || !nav.length) return []
  const site = resolveSiteNode(nav, pageObjName)
  if (!site) return []
  const spec = parseSpec(site.functional_specs)
  const overrides = (spec.page_overrides && typeof spec.page_overrides === 'object'
    ? spec.page_overrides : {}) as Record<string, Record<string, unknown>>
  const chrome = new Set(
    [spec.header_page_obj_name, spec.footer_page_obj_name, spec.popup_page_obj_name]
      .filter((v): v is string => typeof v === 'string' && !!v),
  )
  const byParent = new Map<string, NavItem[]>()
  for (const n of nav) {
    const p = n.parent_id || ''
    if (!byParent.has(p)) byParent.set(p, [])
    byParent.get(p)!.push(n)
  }
  const orderKey = (n: NavItem) => (typeof n.order === 'number' ? n.order : 0)
  const pages: NavItem[] = []
  const walk = (pid: string) => {
    for (const c of (byParent.get(pid) || []).slice().sort((a, b) => orderKey(a) - orderKey(b))) {
      if (c.obj_type === 'S') { if (c.obj_name && !chrome.has(c.obj_name)) pages.push(c) }
      else if (c.obj_type !== 'X') walk(c.id || '')
    }
  }
  walk(site.id || '')
  const explicitHome = pages.find((p) => (overrides[p.obj_name || ''] || {}).is_home === true)?.obj_name
  const homeObj = (typeof spec.home_page_obj_name === 'string' && pages.some((p) => p.obj_name === spec.home_page_obj_name))
    ? spec.home_page_obj_name as string
    : (explicitHome || pages[0]?.obj_name || '')
  return pages.map((p) => {
    const obj = p.obj_name || ''
    const ovr = overrides[obj] || {}
    const coll = ovr.collection && typeof ovr.collection === 'object' ? ovr.collection as { role?: string } : null
    return {
      obj_name: obj,
      slug: obj === homeObj ? '' : (typeof ovr.slug === 'string' ? ovr.slug : slugify(obj)),
      is_home: obj === homeObj,
      label: (typeof ovr.nav_label === 'string' && ovr.nav_label) || p.title || obj,
      collection: coll ? { role: coll.role } : null,
    }
  })
}

/** The raw `menus` spec of the site owning `pageObjName` ({} when none). */
export function siteMenusSpec(nav: NavItem[] | undefined, pageObjName: string): SiteMenusSpec {
  if (!Array.isArray(nav) || !nav.length) return {}
  const site = resolveSiteNode(nav, pageObjName)
  const menus = site ? parseSpec(site.functional_specs).menus : null
  return menus && typeof menus === 'object' && !Array.isArray(menus) ? menus as SiteMenusSpec : {}
}

/** The site's named menus, resolved — what a `nav_menu` with `menu` renders. */
export function buildSiteMenus(nav: NavItem[] | undefined, pageObjName: string): SiteMenus {
  return resolveSiteMenus(siteMenusSpec(nav, pageObjName), buildSitePages(nav, pageObjName))
}

/** The nav node id of the website owning `pageObjName` (for an "Edit menus"
 *  link to the website settings), or '' when the page is not in a site. */
export function siteNodeIdFor(nav: NavItem[] | undefined, pageObjName: string): string {
  if (!Array.isArray(nav) || !nav.length) return ''
  return resolveSiteNode(nav, pageObjName)?.id || ''
}
